import { useEffect, useState } from 'react'
import ReactHtmlParser from 'react-html-parser'
import Icon from '../../../components/icon'
import { removeElementAtIndex } from '../../../helpers'
import { Product } from '../../../redux-toolkits/products/products.type'
import { Section, Sections } from '../../../redux-toolkits/lists/list.types'

export const ArticleSectionLiveView = ({
  section,
  setActiveSectionIndex,
  sectionIndex,
  sections,
  setSections,
  isAnyOtherSectionActive,
  disableActions = false,
  products = [],
}: {
  section: Section
  setActiveSectionIndex: (index: number | null) => void
  sectionIndex: number
  sections: Sections
  setSections: (sections: Sections) => void
  isAnyOtherSectionActive: boolean
  disableActions?: boolean
  products?: Product[]
}) => {
  const moveSection = (direction: 'up' | 'down') => {
    const copy: Sections = JSON.parse(JSON.stringify(sections))
    const targetIdx = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1
    ;[copy[sectionIndex], copy[targetIdx]] = [
      copy[targetIdx],
      copy[sectionIndex],
    ]
    setSections(copy)
  }

  const [allSectionsCopy, setAllSectionsCopy] = useState<Sections>(
    JSON.parse(JSON.stringify(sections)),
  )

  useEffect(() => {
    setAllSectionsCopy(JSON.parse(JSON.stringify(sections)))
  }, [sections])

  const resolveDisplayText = (subSection: (typeof section.content)[number]) => {
    const productPlaceholders =
      subSection.value.match(/__product_[a-f0-9]+__/g) || []

    if (productPlaceholders.length === 0) return subSection.value

    const placeholdersObject = Object.assign(
      {},
      ...productPlaceholders.map((placeholder) => {
        const id = placeholder.split('_')[3]
        const name =
          subSection.products.find((p) => p._id === id)?.name ??
          products.find((p) => p._id === id)?.name
        return {
          [placeholder]: `<a href="/product?id=${id}" class="font-semibold underline underline-offset-[5px] cursor-pointer">${name}</a>`,
        }
      }),
    )

    return subSection.value.replace(
      new RegExp(productPlaceholders.join('|'), 'gi'),
      (matched) => placeholdersObject[matched],
    )
  }

  // Group items so consecutive images are collected into a single grid block,
  // preserving the original text/image ordering.
  type Block =
    | { kind: 'text'; value: string; key: string }
    | { kind: 'images'; items: { src: string; key: string }[] }

  const blocks: Block[] = []
  for (let i = 0; i < (section?.content?.length ?? 0); i++) {
    const item = section.content[i]
    if (item.type === 'text') {
      blocks.push({
        kind: 'text',
        value: resolveDisplayText(item),
        key: `text-${i}`,
      })
    } else {
      const imageGroup: { src: string; key: string }[] = []
      while (
        i < section.content.length &&
        section.content[i].type === 'image'
      ) {
        imageGroup.push({ src: section.content[i].value, key: `image-${i}` })
        i++
      }
      i-- // back up so the outer loop increment lands correctly
      blocks.push({ kind: 'images', items: imageGroup })
    }
  }

  return (
    <div className="border border-divider-gray rounded-md overflow-hidden">
      {/* Section header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-bg/30 border-b border-divider-gray">
        <p className="text-xs font-medium uppercase tracking-widest text-sec-black">
          Section {sectionIndex + 1}
        </p>

        {!isAnyOtherSectionActive && !disableActions && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              title="Move up"
              disabled={sectionIndex === 0}
              onClick={() => moveSection('up')}
              className="p-1.5 rounded-md text-sec-black hover:text-black hover:bg-gray-bg transition-colors disabled:opacity-30 disabled:cursor-not-allowed rotate-180"
            >
              <Icon id="arrow-down-icon" width={15} height={15} />
            </button>

            <button
              type="button"
              title="Move down"
              disabled={sectionIndex === sections.length - 1}
              onClick={() => moveSection('down')}
              className="p-1.5 rounded-md text-sec-black hover:text-black hover:bg-gray-bg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Icon id="arrow-down-icon" width={15} height={15} />
            </button>

            <div className="w-px h-4 bg-divider-gray mx-0.5" />

            <button
              type="button"
              title="Edit section"
              onClick={() => setActiveSectionIndex(sectionIndex)}
              className="p-1.5 rounded-md text-sec-black hover:text-black hover:bg-gray-bg transition-colors"
            >
              <Icon id="edit" width={15} height={15} />
            </button>
            <button
              type="button"
              title="Delete section"
              onClick={() => {
                const temp: Sections = removeElementAtIndex(
                  allSectionsCopy,
                  sectionIndex,
                )
                setSections(temp)
                setActiveSectionIndex(null)
              }}
              className="p-1.5 rounded-md text-sec-black hover:text-danger hover:bg-danger/10 transition-colors"
            >
              <Icon id="bin" width={15} height={15} />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col gap-4">
        {blocks.map((block, i) =>
          block.kind === 'text' ? (
            <div
              key={block.key}
              className="w-full break-words hyphens-auto leading-relaxed text-base"
            >
              {ReactHtmlParser(block.value)}
            </div>
          ) : (
            <div key={`images-${i}`} className="grid grid-cols-2 gap-3">
              {block.items.map((img) => (
                <div key={img.key} className="overflow-hidden">
                  <img
                    src={img.src}
                    alt="good list asset"
                    className="w-full h-full object-contain"
                  />
                </div>
              ))}
            </div>
          ),
        )}
      </div>
    </div>
  )
}
